import React, { useState, useMemo, useEffect, useRef, forwardRef, useImperativeHandle, useId } from 'react';
import { createPortal } from 'react-dom';
import { formatProductDisplayStock, getProductDisplayStock } from '../../utils/productStockDisplay';

/** Mínimo de caracteres antes de abrir la lista (evita colgar con catálogos grandes). */
const MIN_SEARCH_LENGTH = 3;

const meetsMinSearch = (term) => String(term ?? '').trim().length >= MIN_SEARCH_LENGTH;

const ProductSelector = forwardRef(({
  value,
  onChange,
  disabled = false,
  error = null,
  products: externalProducts = null,
  compact = false,
  productStockMap = null,
  productStockMapReady = true,
  asyncSearch = false,
  knownProducts = null,
  onSearchProducts = null,
  onEnsureProduct = null,
  onProductPicked = null,
  formatOptionLabel = null,
  optionBadge = null,
  matchOptionValue = null,
  skipEnsureForValue = null,
  searchEmptyMessage = 'No se encontraron productos',
  searchLoadingMessage = 'Buscando productos...',
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [products, setProducts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchRequestRef = useRef(0);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    bottom: 0,
    placement: 'bottom',
    maxHeight: 384,
  });
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const portalRef = useRef(null);
  const optionRefs = useRef([]);
  const blurCloseTimerRef = useRef(null);
  const suppressBlurCloseRef = useRef(false);
  const portalInstanceId = useId().replace(/:/g, '');
  const selectedProductCacheRef = useRef(null);
  const stockMapPending = productStockMap != null && !productStockMapReady;

  const optionMatchesValue = (item, optionValue) => {
    if (typeof matchOptionValue === 'function') {
      return matchOptionValue(item, optionValue);
    }
    return String(item?.id) === String(optionValue);
  };

  const getOptionLabel = (item) => {
    if (typeof formatOptionLabel === 'function') {
      return formatOptionLabel(item);
    }
    return `${item.name} - ${item.sku}`;
  };

  const updateDropdownPosition = () => {
    if (!inputRef.current) return;

    const rect = inputRef.current.getBoundingClientRect();
    const gap = 4;
    const preferredMaxHeight = 384;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < preferredMaxHeight && spaceAbove > spaceBelow;
    const maxHeight = Math.min(
      preferredMaxHeight,
      Math.max(120, openUp ? spaceAbove : spaceBelow),
    );

    setDropdownPosition({
      left: rect.left,
      width: rect.width,
      placement: openUp ? 'top' : 'bottom',
      maxHeight,
      top: openUp ? undefined : rect.bottom + gap,
      bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
    });
  };

  const openDropdown = (term = searchTerm) => {
    if (!meetsMinSearch(term)) {
      setIsOpen(false);
      return;
    }
    updateDropdownPosition();
    setIsOpen(true);
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (disabled || !inputRef.current) return;
      inputRef.current.focus({ preventScroll: false });
      inputRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    },
  }), [disabled]);

  const cancelScheduledClose = () => {
    if (blurCloseTimerRef.current) {
      window.clearTimeout(blurCloseTimerRef.current);
      blurCloseTimerRef.current = null;
    }
  };

  const scheduleCloseOnBlur = () => {
    cancelScheduledClose();
    blurCloseTimerRef.current = window.setTimeout(() => {
      if (!suppressBlurCloseRef.current) {
        setIsOpen(false);
        if (!value) {
          setSearchTerm('');
        }
      }
    }, 150);
  };

  useEffect(() => {
    if (asyncSearch) {
      if (Array.isArray(knownProducts)) {
        setProducts(knownProducts);
      }
      return;
    }
    if (externalProducts !== null) {
      setProducts(externalProducts);
    }
  }, [asyncSearch, externalProducts, knownProducts]);

  useEffect(() => {
    if (!asyncSearch || typeof onSearchProducts !== 'function') return undefined;
    if (!meetsMinSearch(searchTerm)) {
      setSearchResults([]);
      setSearching(false);
      return undefined;
    }

    const requestId = ++searchRequestRef.current;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      try {
        const list = await onSearchProducts(searchTerm.trim());
        if (searchRequestRef.current === requestId) {
          setSearchResults(Array.isArray(list) ? list : []);
        }
      } catch {
        if (searchRequestRef.current === requestId) {
          setSearchResults([]);
        }
      } finally {
        if (searchRequestRef.current === requestId) {
          setSearching(false);
        }
      }
    }, 280);

    return () => window.clearTimeout(timer);
  }, [asyncSearch, searchTerm, onSearchProducts]);

  useEffect(() => {
    if (!asyncSearch || typeof onEnsureProduct !== 'function' || !value) return;
    if (typeof skipEnsureForValue === 'function' && skipEnsureForValue(value)) return;
    const productId = parseInt(value, 10);
    if (!productId) return;
    if (products.some((product) => product.id === productId)) return;
    onEnsureProduct(productId);
  }, [asyncSearch, value, products, onEnsureProduct]);

  useEffect(() => {
    if (!isOpen) return;
    updateDropdownPosition();
    const onScrollOrResize = () => updateDropdownPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isOpen]);

  useEffect(() => {
    const handlePointerDownOutside = (event) => {
      const root = dropdownRef.current;
      const portal = portalRef.current;
      const insideControl = root?.contains(event.target);
      const insidePortal = portal?.contains(event.target);
      if (!insideControl && !insidePortal) {
        setIsOpen(false);
        if (!value) {
          setSearchTerm('');
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handlePointerDownOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handlePointerDownOutside);
    };
  }, [isOpen, value]);

  useEffect(() => () => cancelScheduledClose(), []);

  const filteredProducts = useMemo(() => {
    if (!meetsMinSearch(searchTerm)) {
      return [];
    }

    if (asyncSearch) {
      return searchResults;
    }

    const search = searchTerm.trim().toLowerCase();
    return products.filter(product => {
      const nameMatch = product.name?.toLowerCase().includes(search);
      const skuMatch = product.sku?.toLowerCase().includes(search);
      const categoryMatch = product.category?.name?.toLowerCase().includes(search);
      const subcategoryMatch = product.subcategory?.name?.toLowerCase().includes(search);
      const familyMatch = product.family?.name?.toLowerCase().includes(search);

      return nameMatch || skuMatch || categoryMatch || subcategoryMatch || familyMatch;
    });
  }, [products, searchTerm, asyncSearch, searchResults]);

  const selectedProduct = useMemo(() => {
    if (!value) {
      selectedProductCacheRef.current = null;
      return null;
    }
    const fromList = (list) => list.find((p) => optionMatchesValue(p, value));
    return (
      fromList(products)
      || fromList(searchResults)
      || (selectedProductCacheRef.current && optionMatchesValue(selectedProductCacheRef.current, value)
        ? selectedProductCacheRef.current
        : null)
    );
  }, [products, value, searchResults, matchOptionValue]);

  useEffect(() => {
    if (!isOpen) {
      setHighlightedIndex(-1);
      return;
    }
    setHighlightedIndex(filteredProducts.length > 0 ? 0 : -1);
  }, [isOpen, searchTerm, filteredProducts.length]);

  useEffect(() => {
    if (highlightedIndex < 0) return;
    const el = optionRefs.current[highlightedIndex];
    if (el?.scrollIntoView) {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, filteredProducts]);

  const handleSelect = (product) => {
    selectedProductCacheRef.current = product;
    if (typeof onProductPicked === 'function') {
      onProductPicked(product);
    }
    onChange(product.id);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
    cancelScheduledClose();
  };

  const clearSelectionForSearch = () => {
    if (selectedProduct) {
      onChange('');
    }
  };

  const handleInputKeyDown = (event) => {
    if (disabled) return;

    const { key } = event;
    const count = filteredProducts.length;

    if (key === 'ArrowDown') {
      event.preventDefault();
      clearSelectionForSearch();
      if (!isOpen) {
        if (!meetsMinSearch(searchTerm)) return;
        openDropdown(searchTerm);
        setHighlightedIndex(count > 0 ? 0 : -1);
        return;
      }
      if (count === 0) return;
      setHighlightedIndex((prev) => (prev < count - 1 ? prev + 1 : 0));
      return;
    }

    if (key === 'ArrowUp') {
      event.preventDefault();
      clearSelectionForSearch();
      if (!isOpen) {
        if (!meetsMinSearch(searchTerm)) return;
        openDropdown(searchTerm);
        setHighlightedIndex(count > 0 ? count - 1 : -1);
        return;
      }
      if (count === 0) return;
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : count - 1));
      return;
    }

    if (key === 'Enter') {
      if (!isOpen || highlightedIndex < 0 || !filteredProducts[highlightedIndex]) return;
      event.preventDefault();
      handleSelect(filteredProducts[highlightedIndex]);
      return;
    }

    if (key === 'Escape') {
      if (!isOpen) return;
      event.preventDefault();
      closeDropdown();
    }
  };

  const handlePortalPointerDown = (event) => {
    event.preventDefault();
    suppressBlurCloseRef.current = true;
    cancelScheduledClose();
  };

  const handlePortalPointerUp = () => {
    window.setTimeout(() => {
      suppressBlurCloseRef.current = false;
    }, 0);
  };

  const displayValue = selectedProduct
    ? getOptionLabel(selectedProduct)
    : (value && !searchTerm ? 'Cargando producto…' : searchTerm);

  return (
    <div className="relative" ref={dropdownRef} style={{ position: 'relative', zIndex: isOpen ? 9999 : 'auto' }}>
      <div className="relative">
        <input
          type="text"
          value={displayValue}
          onChange={(e) => {
            const v = e.target.value;
            setSearchTerm(v);
            if (selectedProduct) {
              onChange('');
            }
            if (meetsMinSearch(v)) {
              openDropdown(v);
            } else {
              closeDropdown();
            }
          }}
          onKeyDown={handleInputKeyDown}
          onFocus={() => {
            cancelScheduledClose();
            if (meetsMinSearch(searchTerm)) {
              openDropdown(searchTerm);
            }
          }}
          onBlur={scheduleCloseOnBlur}
          ref={inputRef}
          placeholder={compact ? 'Buscar producto...' : 'Buscar producto por SKU, nombre o categoría...'}
          disabled={disabled}
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={`product-selector-dropdown-${portalInstanceId}`}
          aria-autocomplete="list"
          aria-activedescendant={
            isOpen && highlightedIndex >= 0 && filteredProducts[highlightedIndex]
              ? `product-option-${portalInstanceId}-${filteredProducts[highlightedIndex].id}`
              : undefined
          }
          className={`w-full ${compact ? 'h-[34px] px-2 py-1.5 text-sm' : 'px-4 py-2.5'} border ${compact ? 'rounded-md focus:ring-1' : 'rounded-lg focus:ring-2'} focus:outline-none transition-all pr-10 ${
            error ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        />
        <button
          type="button"
          tabIndex={-1}
          onMouseDown={(e) => {
            e.preventDefault();
            cancelScheduledClose();
            suppressBlurCloseRef.current = true;
          }}
          onMouseUp={() => {
            window.setTimeout(() => {
              suppressBlurCloseRef.current = false;
            }, 0);
          }}
          onClick={() => {
            if (disabled) return;
            inputRef.current?.focus();
          }}
          disabled={disabled}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          aria-label="Abrir lista de productos"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {isOpen && meetsMinSearch(searchTerm) && !disabled && dropdownPosition.width > 0 && typeof document !== 'undefined' && createPortal(
        <div
          ref={portalRef}
          id={`product-selector-dropdown-${portalInstanceId}`}
          className="bg-white border border-gray-300 rounded-lg shadow-xl overflow-y-auto"
          style={{
            position: 'fixed',
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            minWidth: 200,
            maxHeight: dropdownPosition.maxHeight,
            zIndex: 99999,
            ...(dropdownPosition.placement === 'top'
              ? { bottom: dropdownPosition.bottom }
              : { top: dropdownPosition.top }),
          }}
          role="listbox"
          onMouseDown={handlePortalPointerDown}
          onMouseUp={handlePortalPointerUp}
        >
          {searching || stockMapPending ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              {stockMapPending ? 'Cargando stock del almacén...' : searchLoadingMessage}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              {searchEmptyMessage}
            </div>
          ) : (
            <div className="py-1">
              {filteredProducts.map((product, index) => {
                const stockInfo = productStockMap && (productStockMap[product.id] ?? productStockMap[String(product.id)]);
                const hasMap = productStockMap != null;
                const stock = hasMap
                  ? (stockInfo != null ? getProductDisplayStock(stockInfo) : 0)
                  : null;
                const stockLabel = hasMap
                  ? `Stock: ${formatProductDisplayStock(stockInfo)}`
                  : null;
                const isHighlighted = highlightedIndex === index;
                const isSelected = selectedProduct && optionMatchesValue(product, selectedProduct.id);
                const badge = typeof optionBadge === 'function' ? optionBadge(product) : null;
                return (
                  <button
                    key={product.id}
                    id={`product-option-${portalInstanceId}-${product.id}`}
                    ref={(el) => {
                      optionRefs.current[index] = el;
                    }}
                    type="button"
                    role="option"
                    aria-selected={isHighlighted || isSelected}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onClick={() => handleSelect(product)}
                    className={`w-full text-left px-4 py-2 transition-colors ${
                      isHighlighted
                        ? 'bg-teal-100 ring-1 ring-inset ring-teal-400'
                        : isSelected
                          ? 'bg-teal-50'
                          : 'hover:bg-teal-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900 flex-1">{product.name}</div>
                      {badge && (
                        <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                          {badge}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      <span className="font-medium">{product.catalog_type === 'offer' ? 'Código:' : 'SKU:'}</span>{' '}
                      {product.sku}
                      {stockLabel !== null && (
                        <>
                          {' • '}
                          <span className={
                            Number(stock) < 0
                              ? 'text-red-600 font-semibold'
                              : Number(stock) === 0
                                ? 'text-amber-600 font-medium'
                                : 'text-gray-600'
                          }>{stockLabel}</span>
                        </>
                      )}
                      {product.category && (
                        <>
                          {' • '}
                          <span className="font-medium">Categoría:</span> {product.category.name}
                          {product.subcategory && ` > ${product.subcategory.name}`}
                          {product.family && ` > ${product.family.name}`}
                        </>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>,
        document.body
      )}

      {!selectedProduct && !disabled && searchTerm.trim().length > 0 && searchTerm.trim().length < MIN_SEARCH_LENGTH && (
        <p className="mt-1 text-xs text-gray-500">
          Escriba al menos {MIN_SEARCH_LENGTH} caracteres para buscar
        </p>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
});

ProductSelector.displayName = 'ProductSelector';

export default ProductSelector;
